/**
 * The KnowIt adapter for the notifications inbox and preferences (W9 §A.5, §C.3).
 *
 * Two lanes, on purpose, and the choice is load-bearing (§E-12): `markRead`/`archive`/
 * `markAllRead` ride the **REST twins** (`PUT /api/notifications/{id}`,
 * `PUT /api/notifications/mark-all-read`) because those are the guarded handlers — the dotted
 * RPC route `notifications.update` has **no ownership check and no current-user check at all**
 * (any authenticated caller holding a notification GUID could mark it read/archived), and its
 * sibling `notifications.update_all` hard-codes `data.total = 0`. Both dotted routes are simply
 * never called from here.
 *
 * `preferences()` reads `user.notificationSettings` off `auth.info` — there is no dedicated
 * read endpoint, and the object is scalar-or-per-channel per event id (`resolveChannel` in
 * `notificationEvents.ts` is what a component actually reads through).
 */
import type { Notification, NotificationChannel, NotificationPreferences } from "~/api/types";
import { NotificationSchema } from "~/api/types";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : {};
}
function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export interface NotificationsTransport {
  /** `notifications.list`, `users.notificationsChannel` — `{data}` (or the nested P+D shape). */
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** The two guarded REST twins, bare bodies, 204-tolerant. */
  put(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** `auth.info`'s untyped `data` — where `user.notificationSettings` rides. */
  authInfoRaw(): Promise<Json>;
}

export function createKnowItNotifications(transport: NotificationsTransport) {
  return {
    list: async (
      query: { archived?: boolean; offset?: number; limit?: number } = {},
    ): Promise<{
      pagination: { offset: number; limit: number; total: number };
      notifications: Notification[];
      unseen: number;
    }> => {
      const raw = obj(
        await transport.post("notifications.list", {
          // ⚠ N6: the badge query must send no `eventType` — `unseen` is computed AFTER the
          // eventType/archived filters, so a filtered call would answer a wrong unread count.
          ...(query.archived !== undefined ? { archived: query.archived } : {}),
          offset: query.offset ?? 0,
          limit: query.limit ?? 25,
        }),
      );
      const pagination = obj(raw.pagination);
      const data = obj(raw.data);
      return {
        pagination: {
          offset: typeof pagination.offset === "number" ? pagination.offset : (query.offset ?? 0),
          limit: typeof pagination.limit === "number" ? pagination.limit : (query.limit ?? 25),
          total: typeof pagination.total === "number" ? pagination.total : 0,
        },
        notifications: arr(data.notifications).map((entry) => NotificationSchema.parse(entry)),
        unseen: typeof data.unseen === "number" ? data.unseen : 0,
      };
    },

    /** `PUT /api/notifications/{id}` — the ownership-checked handler (§E-12). */
    markRead: async (id: string): Promise<void> => {
      await transport.put(`notifications/${encodeURIComponent(id)}`, {
        viewedAt: new Date().toISOString(),
      });
    },

    archive: async (id: string): Promise<void> => {
      await transport.put(`notifications/${encodeURIComponent(id)}`, {
        archivedAt: new Date().toISOString(),
      });
    },

    /** `PUT /api/notifications/mark-all-read` — the real count; `notifications.update_all`
     * hard-codes `data.total = 0` and is never called. */
    markAllRead: async (): Promise<number> => {
      const raw = obj(
        await transport.put("notifications/mark-all-read", { viewedAt: new Date().toISOString() }),
      );
      return typeof raw.total === "number" ? raw.total : 0;
    },

    preferences: async (): Promise<NotificationPreferences> => {
      const info = await transport.authInfoRaw();
      const user = obj(info.user);
      const settings = user.notificationSettings;
      return settings && typeof settings === "object" && !Array.isArray(settings)
        ? (settings as NotificationPreferences)
        : {};
    },

    setChannel: async (input: {
      eventType: string;
      channel: NotificationChannel;
      enabled: boolean;
    }): Promise<void> => {
      await transport.post("users.notificationsChannel", input);
    },
  };
}
